/**
 * Central Date Utility for Aarthika Pro
 * Handles standard-style smart date parsing, formatting, and validation.
 *
 * Supported input formats (like Aarthika Pro):
 *   "1"         → 1-[current month]-[current year]
 *   "1-6"       → 1-6-[current year]
 *   "1/6"       → 1-6-[current year]
 *   "1-6-26"    → 1-6-2026
 *   "1-6-2026"  → 1-6-2026
 *   "1 jun"     → 1-6-[current year]
 *   "1jun"      → 1-6-[current year]
 *   "jun"       → 1-6-[current year]
 *   "1 jun 26"  → 1-6-2026
 *   "t" / "today" → today's date
 *   "010626"    → 1-6-2026   (DDMMYY compact)
 *   "01062026"  → 1-6-2026   (DDMMYYYY compact)
 */

const MONTH_MAP: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
  january: 1, february: 2, march: 3, april: 4, june: 6,
  july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
};

/**
 * Converts a string YYYY-MM-DD to a local Date object without time zone shift.
 */
const safeParseDate = (date: string | Date): Date => {
  if (date instanceof Date) return date;
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const parts = date.split('-');
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  }
  return new Date(date);
};

/**
 * Formats a date to standard-style display: D-M-YYYY (no leading zeros, like Aarthika Pro)
 * e.g. 2026-06-01 → "1-6-2026"
 */
export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  const d = safeParseDate(date);
  if (isNaN(d.getTime())) return '';
  return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
};

/**
 * Formats a date to YYYY-MM-DD (Standard for storage and HTML inputs)
 */
export const toStorageDate = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  const d = typeof date === 'string'
    ? (/^\d{4}-\d{2}-\d{2}$/.test(date) ? safeParseDate(date) : parseSmartDate(date))
    : safeParseDate(date);
  if (!d || isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

/**
 * Aarthika Pro style smart date parsing.
 * Accepts virtually any shorthand a user might type.
 */
export const parseSmartDate = (input: string, baseDate: Date = new Date()): Date | null => {
  if (!input || typeof input !== 'string') return null;

  const raw = input.trim().toLowerCase();
  if (!raw) return null;

  // "today" / "t"
  if (raw === 't' || raw === 'today') return new Date(baseDate);

  let day = baseDate.getDate();
  let month = baseDate.getMonth() + 1; // 1-based
  let year = baseDate.getFullYear();

  // ── Step 1: Try inline formats like "1jun", "1jun26", "1jun2026", "jun" ──
  // Pattern: optional digits, then letters, then optional digits (year)
  const inlineMatch = raw.match(/^(\d{1,2})?([a-z]+)(\d{2,4})?$/);
  if (inlineMatch) {
    const mName = MONTH_MAP[inlineMatch[2]];
    if (mName !== undefined) {
      if (inlineMatch[1]) day = parseInt(inlineMatch[1]);
      else day = 1;
      month = mName;
      if (inlineMatch[3]) {
        const y = parseInt(inlineMatch[3]);
        year = y < 100 ? 2000 + y : y;
      }
      return _makeDate(year, month, day);
    }
  }

  // ── Step 2: Compact numeric "010626" / "01062026" ──
  if (/^\d+$/.test(raw)) {
    if (raw.length <= 2) {
      day = parseInt(raw);
    } else if (raw.length === 4) {
      day = parseInt(raw.substring(0, 2));
      month = parseInt(raw.substring(2, 4));
    } else if (raw.length === 6) {
      day = parseInt(raw.substring(0, 2));
      month = parseInt(raw.substring(2, 4));
      year = 2000 + parseInt(raw.substring(4, 6));
    } else if (raw.length === 8) {
      day = parseInt(raw.substring(0, 2));
      month = parseInt(raw.substring(2, 4));
      year = parseInt(raw.substring(4, 8));
    }
    return _makeDate(year, month, day);
  }

  // ── Step 3: Separator-based "1-6", "1-6-26", "1 jun 26", "1 june 2026" ──
  const parts = raw.split(/[\s\-\/\.]+/).filter(Boolean);

  if (parts.length === 1) {
    // Already handled above (pure digits or inline month)
    return null;
  }

  if (parts.length >= 2) {
    // First part is always day
    day = parseInt(parts[0]);
    if (isNaN(day)) return null;

    // Second part: month name or number
    const mName = MONTH_MAP[parts[1]];
    if (mName !== undefined) {
      month = mName;
    } else {
      const mNum = parseInt(parts[1]);
      if (isNaN(mNum)) return null;
      month = mNum;
    }

    // Third part: year (optional)
    if (parts.length >= 3) {
      const y = parseInt(parts[2]);
      if (!isNaN(y)) year = y < 100 ? 2000 + y : y;
    }
  }

  return _makeDate(year, month, day);
};

/** Build a local Date and validate it (rejects impossible dates like 31-Feb). */
function _makeDate(year: number, month: number, day: number): Date | null {
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  if (day < 1 || day > 31 || month < 1 || month > 12) return null;
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return d;
}

/**
 * Validates if a string is a valid date in DD-MM-YYYY or YYYY-MM-DD format
 */
export const isValidDate = (dateStr: string): boolean => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  return !isNaN(d.getTime());
};

/**
 * Returns the financial year for a given date (Starts April 1st)
 */
export const getFinancialYear = (date: Date = new Date()) => {
  const month = date.getMonth();
  const year = date.getFullYear();
  
  if (month >= 3) { // April onwards
    return { start: year, end: year + 1 };
  } else {
    return { start: year - 1, end: year };
  }
};
